import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PiArrowsClockwiseBold, PiCheckCircleBold, PiClockCountdownBold, PiWarningBold } from 'react-icons/pi';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';
import toast from 'react-hot-toast';

const brl = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v ?? 0);

type PreviewData = {
    records_count: number;
    deals_count: number;
    total_amount: number;
    deals_sample: { id: number; title: string; amount: number; finish_date: string; owner_name: string }[];
};

export default function LegacyMarkPaidPanel() {
    const queryClient = useQueryClient();
    const [cutoffDate, setCutoffDate] = useState('2026-08-01');
    const [paidAt, setPaidAt] = useState('finish_date');
    const [showConfirm, setShowConfirm] = useState(false);

    // Alinha o corte padrão à data configurável de início das NFs.
    useQuery({
        queryKey: ['commissioning', 'settings'],
        queryFn: async () => {
            const res = await api.get('/commissioning/settings');
            const item = (res.data.data as { key: string; value: string }[])
                ?.find((s) => s.key === 'nf_start_date');
            if (item?.value) {
                setCutoffDate(item.value.slice(0, 10));
            }
            return res.data.data;
        },
        staleTime: 60_000,
    });

    const { data: preview, isFetching: previewing, refetch: fetchPreview } = useQuery<PreviewData>({
        queryKey: ['legacy-preview', cutoffDate],
        queryFn: async () => {
            const res = await api.get('/commissioning/legacy/preview', { params: { cutoff_date: cutoffDate } });
            return res.data.data;
        },
        enabled: false,
    });

    const { mutate: markPaid, isPending: marking } = useMutation({
        mutationFn: () =>
            api.post('/commissioning/legacy/mark-paid', { cutoff_date: cutoffDate, paid_at: paidAt }),
        onSuccess: (res) => {
            toast.success(`${res.data.data.updated} registros marcados como pagos.`);
            setShowConfirm(false);
            queryClient.invalidateQueries({ queryKey: ['commission-dashboard'] });
            queryClient.invalidateQueries({ queryKey: ['legacy-preview'] });
        },
        onError: () => toast.error('Erro ao processar legados.'),
    });

    return (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/40 p-5 dark:border-amber-500/20 dark:bg-amber-500/5">
            <div className="mb-4 flex items-center gap-2">
                <PiClockCountdownBold className="size-5 text-amber-600 dark:text-amber-400" />
                <h3 className="text-sm font-black uppercase tracking-wider text-amber-800 dark:text-amber-300">
                    Deals Legados — Marcar como Pago
                </h3>
            </div>

            <p className="mb-4 text-xs text-amber-700 dark:text-amber-400">
                Preferência: use <strong>Configurações → Início das NFs</strong> + sync pré-NF
                (marca cada parcela na <code>due_date</code>). Este painel é o atalho antigo
                (record do deal inteiro numa data só).
            </p>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                    <label className="mb-1 block text-[10px] font-black uppercase tracking-widest text-gray-500 dark:text-zinc-500">
                        Data de corte (deals ganhos antes de)
                    </label>
                    <input
                        type="date"
                        value={cutoffDate}
                        onChange={(e) => { setCutoffDate(e.target.value); setShowConfirm(false); }}
                        className="w-full rounded-xl border border-amber-200 bg-white px-3 py-2 text-sm text-gray-800 outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400/30 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200"
                    />
                </div>
                <div>
                    <label className="mb-1 block text-[10px] font-black uppercase tracking-widest text-gray-500 dark:text-zinc-500">
                        Marcar <code>paid_at</code> como
                    </label>
                    <select
                        value={paidAt}
                        onChange={(e) => { setPaidAt(e.target.value); setShowConfirm(false); }}
                        className="w-full rounded-xl border border-amber-200 bg-white px-3 py-2 text-sm text-gray-800 outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400/30 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200"
                    >
                        <option value="finish_date">Data de ganho do deal (finish_date)</option>
                        <option value={new Date().toISOString().slice(0, 10)}>Hoje ({new Date().toLocaleDateString('pt-BR')})</option>
                    </select>
                    <p className="mt-1 text-[10px] text-gray-400 dark:text-zinc-600">
                        Ou digite uma data específica:&nbsp;
                        <input
                            type="date"
                            value={paidAt === 'finish_date' ? '' : paidAt}
                            placeholder="YYYY-MM-DD"
                            onChange={(e) => { if (e.target.value) { setPaidAt(e.target.value); setShowConfirm(false); } }}
                            className="inline-block w-36 rounded border border-gray-200 px-2 py-0.5 text-xs dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200"
                        />
                    </p>
                </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
                <button
                    type="button"
                    onClick={() => fetchPreview()}
                    disabled={previewing || !cutoffDate}
                    className="inline-flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-100 px-4 py-2 text-xs font-semibold text-amber-800 transition hover:bg-amber-200 disabled:opacity-50 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
                >
                    {previewing ? <PiArrowsClockwiseBold className="size-3.5 animate-spin" /> : <PiClockCountdownBold className="size-3.5" />}
                    Pré-visualizar
                </button>

                {preview && (
                    <button
                        type="button"
                        onClick={() => setShowConfirm(true)}
                        disabled={marking || preview.records_count === 0}
                        className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-4 py-2 text-xs font-semibold text-white transition hover:bg-amber-600 disabled:opacity-50"
                    >
                        <PiCheckCircleBold className="size-3.5" />
                        Processar {preview.records_count} registro(s)
                    </button>
                )}
            </div>

            {/* Resultado da pré-visualização */}
            {preview && (
                <div className="mt-4 rounded-xl border border-amber-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
                    <div className="mb-3 flex flex-wrap gap-4">
                        <Stat label="Registros" value={String(preview.records_count)} color="amber" />
                        <Stat label="Deals únicos" value={String(preview.deals_count)} color="amber" />
                        <Stat label="Total R$" value={brl(preview.total_amount)} color="emerald" />
                    </div>
                    {preview.records_count === 0 && (
                        <p className="text-xs text-gray-500">Nenhum registro elegível para esta data de corte.</p>
                    )}
                    {preview.deals_sample.length > 0 && (
                        <div className="max-h-40 overflow-y-auto">
                            <table className="w-full text-xs">
                                <thead className="text-[10px] uppercase tracking-wide text-gray-400">
                                    <tr>
                                        <th className="pb-1 pr-3 text-left">Deal</th>
                                        <th className="pb-1 pr-3 text-left">Responsável</th>
                                        <th className="pb-1 pr-3 text-right">Valor</th>
                                        <th className="pb-1 text-right">Ganho em</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                                    {preview.deals_sample.map((d) => (
                                        <tr key={d.id}>
                                            <td className="py-1 pr-3 text-gray-800 dark:text-zinc-200">{d.title}</td>
                                            <td className="py-1 pr-3 text-gray-500">{d.owner_name || '—'}</td>
                                            <td className="py-1 pr-3 text-right font-mono">{brl(d.amount)}</td>
                                            <td className="py-1 text-right text-gray-500">{d.finish_date}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Confirm dialog inline */}
            {showConfirm && preview && preview.records_count > 0 && (
                <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-4 dark:border-rose-500/30 dark:bg-rose-500/10">
                    <div className="mb-3 flex items-center gap-2">
                        <PiWarningBold className="size-5 text-rose-600 dark:text-rose-400" />
                        <p className="text-sm font-black text-rose-800 dark:text-rose-300">
                            Confirmar processamento de {preview.records_count} registros?
                        </p>
                    </div>
                    <p className="mb-3 text-xs text-rose-700 dark:text-rose-400">
                        Esta ação marcará todos os commission_records elegíveis como <strong>status=paid</strong>. Não pode ser desfeita automaticamente.
                    </p>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => markPaid()}
                            disabled={marking}
                            className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-4 py-2 text-xs font-black text-white hover:bg-rose-700 disabled:opacity-50"
                        >
                            {marking ? <PiArrowsClockwiseBold className="size-3.5 animate-spin" /> : <PiCheckCircleBold className="size-3.5" />}
                            Confirmar
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowConfirm(false)}
                            className="rounded-xl border border-gray-300 px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 dark:border-zinc-600 dark:text-zinc-300"
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function Stat({ label, value, color }: { label: string; value: string; color: 'amber' | 'emerald' }) {
    return (
        <div className={cn(
            'rounded-xl px-3 py-2 text-center',
            color === 'amber' ? 'bg-amber-50 dark:bg-amber-500/10' : 'bg-emerald-50 dark:bg-emerald-500/10',
        )}>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{label}</p>
            <p className={cn('text-lg font-black', color === 'amber' ? 'text-amber-700 dark:text-amber-300' : 'text-emerald-700 dark:text-emerald-300')}>
                {value}
            </p>
        </div>
    );
}
